class ProductFinancing extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.closest('form');
    this.link = this.querySelector('[data-shop-pay-link]');
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.product-financing__content');
    if (!this.form || !this.link) return;

    this.expanded = this.details.open;
    this.summary.addEventListener('click', (event) => this.toggle(event), { signal });
    this.details.addEventListener('toggle', () => {
      if (!this.animation) this.expanded = this.details.open;
    }, { signal });

    const section = this.closest('.shopify-section');
    section?.addEventListener('variant:selected', () => {
      this.variantAvailable = false;
      this.setUnavailable();
    }, { signal });
    section?.addEventListener('variant:update', (event) => {
      const productForm = this.closest('product-form-component');
      if (event.detail.data.productId !== productForm?.dataset.productId && !event.detail.data.newProduct) return;
      queueMicrotask(() => {
        this.variantAvailable = Boolean(event.detail.resource?.available);
        this.updateLink();
        this.form.querySelector('input[name="id"]')?.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }, { signal });
    this.form.addEventListener('change', () => this.updateLink(), { signal });
    this.form.addEventListener('input', () => this.updateLink(), { signal });
    this.link.addEventListener('click', (event) => {
      this.updateLink();
      if (this.link.getAttribute('aria-disabled') === 'true' || !this.form.reportValidity()) event.preventDefault();
    }, { signal });
    this.querySelector('approve-button')?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (!event.repeat) event.currentTarget.shadowRoot?.querySelector('[button]')?.click();
    }, { signal });
    this.variantAvailable = this.link.getAttribute('aria-disabled') !== 'true';
    this.updateLink();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.animation?.cancel();
    this.finishToggle();
  }

  toggle(event) {
    event.preventDefault();
    const startHeight = this.details.getBoundingClientRect().height;
    this.animation?.cancel();
    this.expanded = !this.expanded;
    this.details.style.height = `${startHeight}px`;
    this.details.style.overflow = 'hidden';
    this.details.toggleAttribute('data-closing', !this.expanded);
    if (this.expanded) this.details.open = true;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.details.animate) {
      this.finishToggle();
      return;
    }

    const style = getComputedStyle(this.details);
    const borderHeight = Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth);
    const endHeight = this.summary.getBoundingClientRect().height + borderHeight +
      (this.expanded ? this.content.getBoundingClientRect().height : 0);
    const animation = this.details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 260, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'forwards' }
    );
    this.animation = animation;
    animation.onfinish = () => {
      if (this.animation === animation) this.finishToggle();
    };
  }

  finishToggle() {
    if (!this.details) return;
    this.details.open = Boolean(this.expanded);
    this.animation?.cancel();
    this.animation = null;
    this.details.style.removeProperty('height');
    this.details.style.removeProperty('overflow');
    this.details.removeAttribute('data-closing');
  }

  setUnavailable() {
    this.link.removeAttribute('href');
    this.link.setAttribute('aria-disabled', 'true');
    this.link.setAttribute('tabindex', '-1');
  }

  updateLink() {
    const data = new FormData(this.form);
    const variant = String(data.get('id') || '');
    const quantity = Math.max(1, Number.parseInt(String(data.get('quantity') || '1'), 10) || 1);
    if (!this.variantAvailable || !/^\d+$/.test(variant)) {
      this.setUnavailable();
      return;
    }
    this.link.href = `${this.link.dataset.cartUrl}/${variant}:${quantity}?payment=shop_pay`;
    this.link.removeAttribute('aria-disabled');
    this.link.removeAttribute('tabindex');
  }
}

if (!customElements.get('product-financing')) customElements.define('product-financing', ProductFinancing);

customElements.whenDefined('approve-plugin').then(() => {
  const root = document.querySelector('approve-plugin')?.shadowRoot;
  if (!root || root.querySelector('[data-zetech-financing-style]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-zetech-financing-style', '');
  style.textContent = '.open-tab { display: none !important; }';
  root.appendChild(style);
});
